/**
 * Serato Hot-Cue reader — parses ID3v2 GEOB "Serato Markers2" from an MP3
 * File and returns an array of hot cues. See /app/memory/SERATO_HOTCUE_SPEC.md
 * for the byte-level format.
 *
 * Usage:
 *   const cues = await readSeratoHotCues(fileFromPicker);
 *   // cues: [{ index, position_seconds, color, name }, ...]
 */

function readAscii(view, offset, length) {
  let out = "";
  for (let i = 0; i < length; i++) out += String.fromCharCode(view.getUint8(offset + i));
  return out;
}

function readCString(view, offset, maxLen = 256) {
  let out = "";
  for (let i = 0; i < maxLen; i++) {
    const b = view.getUint8(offset + i);
    if (b === 0) break;
    out += String.fromCharCode(b);
  }
  return out;
}

/** ID3v2 "syncsafe" 28-bit big-endian int (bit 7 always cleared). */
function readSyncsafeInt(view, offset) {
  return (view.getUint8(offset) << 21) |
         (view.getUint8(offset + 1) << 14) |
         (view.getUint8(offset + 2) << 7) |
         view.getUint8(offset + 3);
}

/** Parse the decoded Serato Markers2 payload into a hot-cue list. */
function parseSeratoMarkers2(bin) {
  const view = new DataView(bin.buffer, bin.byteOffset, bin.byteLength);
  const cues = [];
  let offset = 2; // skip 2 version bytes
  while (offset < bin.byteLength - 5) {
    const type = readCString(view, offset);
    if (!type) break;
    offset += type.length + 1;
    if (offset + 4 > bin.byteLength) break;
    const size = view.getUint32(offset);
    offset += 4;
    if (offset + size > bin.byteLength) break;
    if (type === "CUE" && size >= 14) {
      const idx = view.getUint8(offset + 2);
      const posMs = view.getUint32(offset + 3);
      const r = view.getUint8(offset + 8);
      const g = view.getUint8(offset + 9);
      const b = view.getUint8(offset + 10);
      const name = readCString(view, offset + 13, size - 13);
      cues.push({
        index: idx,
        position_seconds: posMs / 1000,
        color: `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`,
        name,
      });
    }
    offset += size;
  }
  return cues.sort((a, b) => a.index - b.index);
}

/**
 * Read Serato hot cues from an MP3 File. Returns [] gracefully on any
 * failure (missing tag, non-Serato file, corrupt data, non-MP3 container).
 */
export async function readSeratoHotCues(file) {
  try {
    // ID3v2 header lives at file byte 0. 1MB is more than enough for tags
    // that carry waveform overview PNGs.
    const bytes = Math.min(file.size, 1024 * 1024);
    const buf = await file.slice(0, bytes).arrayBuffer();
    const view = new DataView(buf);
    if (buf.byteLength < 10) return [];
    // "ID3" magic + version 3.x or 4.x
    if (readAscii(view, 0, 3) !== "ID3") return [];
    const majorVersion = view.getUint8(3);
    const tagSize = readSyncsafeInt(view, 6);
    const tagEnd = Math.min(10 + tagSize, buf.byteLength);

    let offset = 10;
    while (offset < tagEnd - 10) {
      const frameId = readAscii(view, offset, 4);
      // Frame size: syncsafe for v2.4, plain for v2.3
      const frameSize = majorVersion >= 4
        ? readSyncsafeInt(view, offset + 4)
        : view.getUint32(offset + 4);
      // End-of-frames sentinel or garbage → stop
      if (!/^[A-Z0-9]{4}$/.test(frameId) || frameSize === 0 || offset + 10 + frameSize > tagEnd) break;
      offset += 10;
      if (frameId === "GEOB") {
        // GEOB body: [encoding:1][mime:cstr][filename:cstr][description:cstr][payload]
        let p = offset + 1; // skip encoding byte
        const mime = readCString(view, p); p += mime.length + 1;
        const filename = readCString(view, p); p += filename.length + 1;
        const desc = readCString(view, p); p += desc.length + 1;
        if (desc === "Serato Markers2") {
          const payloadLen = frameSize - (p - offset);
          const rawBase64 = readAscii(view, p, payloadLen).replace(/\s/g, "");
          try {
            const bin = Uint8Array.from(atob(rawBase64), c => c.charCodeAt(0));
            return parseSeratoMarkers2(bin);
          } catch { return []; }
        }
      }
      offset += frameSize;
    }
    return [];
  } catch { return []; }
}
